// src/sections/admin/LicensesSection.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ResourceTable from '../../components/admin/ResourceTable'; // Reusable table
import LicenseForm from '../../components/admin/LicenseForm';     // License form

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const LicensesSection = ({ user, licenses = [], setMsg, fetchAll }) => { // Assuming 'licenses' prop passed from AdminPage
  const [showForm, setShowForm] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);

  const adminTenantId = user?.tenant_id != null ? parseInt(user.tenant_id, 10) : null; 

  // Define table columns for Licenses
  const licenseColumns = [
    { key: 'id', label: 'ID' },
    { key: 'license_key', label: 'Key' },
    { key: 'license_type', label: 'Type' },
    { 
      key: 'valid_until', 
      label: 'Valid Until', 
      render: (dateString) => dateString ? new Date(dateString).toLocaleDateString() : 'N/A' 
    },
    { key: 'max_tokens_per_month', label: 'Max Tokens', render: (val) => val || 'N/A' },
    { key: 'current_month_tokens_issued', label: 'Used (Month)', render: (val) => val === null ? 'N/A' : val }, // Can be null if type is not monthly_limit
    { key: 'activated', label: 'Active', render: (val) => val ? 'Yes' : 'No' },
    { key: 'tenant_id', label: 'Tenant ID' },
  ];

  // Handlers for Add/Edit/Delete actions
  const handleAddLicense = () => {
    setEditingLicense(null); 
    setShowForm(true);
  };

  const handleEditLicense = (licenseData) => {
    setEditingLicense(licenseData);
    setShowForm(true);
  };

  const handleDeleteLicense = async (licenseData) => {
    if (window.confirm(`Are you sure you want to delete license ${licenseData.license_key}?`)) {
      try {
        await axios.delete(`${BASE_URL}/api/licenses/${licenseData.id}`);
        setMsg({ type: 'success', text: `License ${licenseData.license_key} deleted successfully!` });
        fetchAll(); 
      } catch (error) {
        setMsg({ type: 'error', text: `Failed to delete license: ${error.message}` });
        console.error("Error deleting license:", error);
      }
    }
  };

  // Handler for submitting the LicenseForm (add or update)
  const handleSubmitLicenseForm = async (formData, isEditMode) => {
    try {
      const dataToSubmit = { ...formData };
      
      // Ensure tenant_id is correctly set for new licenses
      if (!isEditMode) {
        dataToSubmit.tenant_id = adminTenantId; 
      }

      // API calls for License CRUD
      if (isEditMode) {
        await axios.patch(`${BASE_URL}/api/licenses/${formData.id}`, dataToSubmit);
        setMsg({ type: 'success', text: `License ${formData.license_key} updated successfully!` });
      } else {
        await axios.post(`${BASE_URL}/api/licenses`, dataToSubmit);
        setMsg({ type: 'success', text: `License ${formData.license_key} added successfully!` });
      }
      setShowForm(false);
      setEditingLicense(null);
      fetchAll(); // Re-fetch all data to update the table
    } catch (error) {
      const errorMessage = error.response && error.response.data && error.response.data.error
                           ? error.response.data.error 
                           : error.message || "An unknown error occurred."; 
      setMsg({ type: 'error', text: `Failed to ${isEditMode ? 'update' : 'add'} license: ${errorMessage}.` });
      console.error("Error submitting license form:", error);
    }
  };

  // Patch: Only vendor_admin sees all, others see only own-tenant licenses
  let filteredLicenses = licenses;
  if (user?.role === 'vendor_admin' || String(user?.tenant_id) === '999') {
    filteredLicenses = licenses;
  } else {
    filteredLicenses = licenses.filter(l => String(l.tenant_id) === String(adminTenantId));
  }

  return (
    <div>
      <h2 className="admin-section-header">License Management</h2>
      {!showForm ? (
        <>
          <button onClick={handleAddLicense} className="admin-button primary" style={{ marginBottom: '1.5rem' }}>Add New License</button>
          <ResourceTable 
            data={filteredLicenses} 
            columns={licenseColumns} 
            onEdit={handleEditLicense} 
            onDelete={handleDeleteLicense} 
            title="Licenses"
          />
        </>
      ) : (
        <LicenseForm 
          user={user} 
          initialData={editingLicense || {}} 
          onSubmit={handleSubmitLicenseForm} 
          onCancel={() => setShowForm(false)} 
        />
      )}
    </div>
  );
};

export default LicensesSection;